import * as React from 'react'
import { useEffect, useState } from 'react'
import { addMonths, subMonths, format } from 'date-fns'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { monthKeys } from '@/modules/shared/constants/months'
import { DateCalendar } from '@/ui/date/DateCalendar'
import { FaIcon, faChevronLeft, faChevronRight } from '@/lib/fa'
import {
  faChevronDoubleLeft,
  faChevronDoubleRight,
} from '@fortawesome/pro-light-svg-icons'

type DatePickerSingleProps = {
  value?: string | null
  submit: (fullDate: string) => void
  setPreview?: (fullDate: string) => void
  confirmSelection?: boolean
}

const parseDateString = (value: string): Date => {
  const [y, m, d] = value.split('-').map(Number)
  return new Date(y, m - 1, d)
}

export const DatePickerPopup: React.FC<DatePickerSingleProps> = ({
  value,
  submit,
  setPreview,
  confirmSelection = false,
}) => {
  const { t } = useLocalize()

  const [selectedDate, setSelectedDate] = useState<Date | null>(
    value ? parseDateString(value) : null,
  )

  const [currentDate, setCurrentDate] = useState<Date>(
    value ? parseDateString(value) : new Date(),
  )

  // sync when external value changes
  useEffect(() => {
    if (!value) return
    const d = parseDateString(value)
    setSelectedDate(d)
    setCurrentDate(d)
  }, [value])

  const handleDayClick = (day: Date) => {
    setSelectedDate(day)

    const formatted = format(day, 'YYYY-MM-DD')

    if (!confirmSelection) {
      submit(formatted)
    } else {
      setPreview?.(formatted)
    }
  }

  const handleSubmit = () => {
    if (!selectedDate) return
    submit(format(selectedDate, 'YYYY-MM-DD'))
  }

  return (
    <div className="tw-w-[350px] tw-rounded-xl tw-bg-white tw-p-2 tw-shadow">
      {/* HEADER */}
      <div className="tw-flex tw-items-center tw-justify-between tw-p-4">
        <button onClick={() => setCurrentDate(subMonths(currentDate, 12))}>
          <FaIcon icon={faChevronDoubleLeft} />
        </button>

        <FaIcon
          icon={faChevronLeft}
          className="tw-cursor-pointer"
          onClick={() => setCurrentDate(subMonths(currentDate, 1))}
        />

        <p className="tw-font-semibold">
          {t(monthKeys[currentDate.getMonth()])} {currentDate.getFullYear()}
        </p>

        <FaIcon
          icon={faChevronRight}
          className="tw-cursor-pointer"
          onClick={() => setCurrentDate(addMonths(currentDate, 1))}
        />

        <button onClick={() => setCurrentDate(addMonths(currentDate, 12))}>
          <FaIcon icon={faChevronDoubleRight} />
        </button>
      </div>

      {/* CALENDAR */}
      <DateCalendar
        currentMonth={currentDate}
        startDate={selectedDate}
        type="singleDate"
        handleDayClick={handleDayClick}
      />

      {confirmSelection && (
        <div className="tw-flex tw-justify-end tw-p-3">
          <button
            onClick={handleSubmit}
            disabled={!selectedDate}
            className={`tw-rounded-lg tw-px-4 tw-py-2 tw-text-sm ${
              selectedDate
                ? 'tw-bg-primary tw-text-white'
                : 'tw-bg-gray-200 tw-text-gray-400'
            }`}
          >
            {t('operation.confirm')}
          </button>
        </div>
      )}
    </div>
  )
}
